import React, { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import { API_BASE_URL } from '../../config';

// Price per user per month (INR) — must match pricing page
const PLAN_PRICE_PER_USER = {
  Starter: 150,
  Professional: 200,
};

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    if (document.getElementById('razorpay-checkout-js')) {
      const existing = document.getElementById('razorpay-checkout-js');
      existing.onload = () => resolve(true);
      existing.onerror = () => resolve(false);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// Normalize a raw subdomain input: lowercase, only alphanumeric + hyphens
function normalizeSubdomain(value) {
  return value.toLowerCase().replace(/[^a-z0-9-]/g, '');
}

// Derive the effective subdomain the same way the backend does
function deriveSubdomain(domain, company) {
  if (domain) return domain;
  if (company) return company.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9-]/g, '').slice(0, 4);
  return '';
}

// idle | checking | available | taken | invalid
const SUBDOMAIN_STATUS = { IDLE: 'idle', CHECKING: 'checking', AVAILABLE: 'available', TAKEN: 'taken', INVALID: 'invalid' };

const LeadForm = forwardRef(({ initialInterest, initialPlan, isOpen, onClose }, ref) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [userCount, setUserCount] = useState(1);

  const initialFormData = {
    name: '',
    email: '',
    company_name: '',
    phone: '',
    message: '',
    company: '',
    contactEmail: '',
    contactPhone: '',
    domain: '',
    user_name: '',
    user_email: '',
    password: '',
    registrationMessage: '',
  };

  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [subdomainStatus, setSubdomainStatus] = useState(SUBDOMAIN_STATUS.IDLE);
  const debounceRef = useRef(null);

  // Pre-load Razorpay script as soon as subscription modal opens
  useEffect(() => {
    if (isOpen && initialInterest === 'Subscription') {
      loadRazorpayScript();
    }
  }, [isOpen, initialInterest]);

  const handleClose = () => {
    setFormData(initialFormData);
    setCurrentStep(1);
    setUserCount(1);
    setIsSubmitting(false);
    setShowSuccess(false);
    setSuccessData(null);
    setSubdomainStatus(SUBDOMAIN_STATUS.IDLE);
    onClose();
  };

  const handleChange = (e) => {
    const { id, name, value } = e.target;
    setFormData(prev => ({ ...prev, [name || id]: value }));
  };

  // Check subdomain availability with 400 ms debounce
  const triggerSubdomainCheck = useCallback((domain, company) => {
    const effective = deriveSubdomain(domain, company);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!effective || effective.length < 2) {
      setSubdomainStatus(SUBDOMAIN_STATUS.IDLE);
      return;
    }

    // Client-side format check: 2–50 chars, alphanumeric + hyphens, no leading/trailing hyphen
    if (!/^[a-z0-9][a-z0-9-]{0,48}[a-z0-9]$|^[a-z0-9]{2}$/.test(effective)) {
      setSubdomainStatus(SUBDOMAIN_STATUS.INVALID);
      return;
    }

    setSubdomainStatus(SUBDOMAIN_STATUS.CHECKING);

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/check-subdomain?subdomain=${encodeURIComponent(effective)}`
        );
        const data = await res.json();
        if (data.success && data.data?.available) {
          setSubdomainStatus(SUBDOMAIN_STATUS.AVAILABLE);
        } else {
          setSubdomainStatus(
            data.data?.reason === 'invalid_format'
              ? SUBDOMAIN_STATUS.INVALID
              : SUBDOMAIN_STATUS.TAKEN
          );
        }
      } catch {
        setSubdomainStatus(SUBDOMAIN_STATUS.IDLE);
      }
    }, 400);
  }, []);

  // Re-check whenever domain or company changes (domain field depends on both)
  useEffect(() => {
    if (initialInterest !== 'Subscription' || currentStep !== 1) return;
    triggerSubdomainCheck(formData.domain, formData.company);
  }, [formData.domain, formData.company, initialInterest, currentStep, triggerSubdomainCheck]);

  // Normalized domain field handler
  const handleDomainChange = (e) => {
    const normalized = normalizeSubdomain(e.target.value);
    setFormData(prev => ({ ...prev, domain: normalized }));
  };

  // Derived amount values
  const pricePerUser = PLAN_PRICE_PER_USER[initialPlan] ?? 0;
  const totalINR = pricePerUser * userCount;
  const totalPaise = totalINR * 100; // Razorpay expects paise
  const finalDomain = () => deriveSubdomain(formData.domain, formData.company);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ── Demo request ──────────────────────────────────────────────────────────
    if (initialInterest === 'Demo') {
      setIsSubmitting(true);
      try {
        const response = await fetch(`${API_BASE_URL}/admin/demo-request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            company: formData.company_name,
            phone: formData.phone,
            message: formData.message || 'Demo request from landing page',
          }),
        });
        const result = await response.json();
        if (result.success) {
          alert("Demo request submitted! We'll contact you soon.");
          handleClose();
        } else {
          alert('Error submitting demo request. Please try again.');
        }
      } catch (err) {
        console.error(err);
        alert('Error occurred while submitting demo request.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // ── Subscription: advance steps 1 & 2 ────────────────────────────────────
    if (currentStep < 3) {
      // Block step 1 → 2 if subdomain is taken or invalid
      if (currentStep === 1 && (subdomainStatus === SUBDOMAIN_STATUS.TAKEN || subdomainStatus === SUBDOMAIN_STATUS.INVALID)) {
        return;
      }
      setCurrentStep(currentStep + 1);
      return;
    }

    // ── Step 3: create order → open Razorpay → verify & register ─────────────
    setIsSubmitting(true);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded || !window.Razorpay) {
        alert('Razorpay failed to load. Please check your internet connection and try again.');
        setIsSubmitting(false);
        return;
      }

      // 1. Ask backend to create a Razorpay order; amount comes from frontend
      const orderRes = await fetch(`${API_BASE_URL}/payment/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: totalPaise,
          name: formData.user_name || formData.company,
          email: formData.contactEmail,
          phone: formData.contactPhone,
        }),
      });

      const orderData = await orderRes.json();

      if (!orderRes.ok || !orderData.success) {
        alert(orderData.message || 'Failed to initiate payment. Please try again.');
        setIsSubmitting(false);
        return;
      }

      const { order_id, amount, currency, key_id } = orderData.data;

      // 2. Open Razorpay checkout
      const options = {
        key: key_id,
        amount,
        currency,
        name: 'ApexMed',
        description: `${initialPlan} Plan — ${userCount} user${userCount > 1 ? 's' : ''} × ₹${pricePerUser}/month`,
        order_id,
        prefill: {
          name: formData.user_name || formData.company,
          email: formData.contactEmail,
          contact: formData.contactPhone,
        },
        theme: { color: '#00a9c3' },
        modal: {
          ondismiss: () => setIsSubmitting(false),
        },
        handler: async (razorpayResponse) => {
          // 3. Verify signature on backend, then register tenant
          try {
            const verifyRes = await fetch(
              `${API_BASE_URL}/payment/verify-and-register`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  // Payment proof — backend verifies signature before registering
                  razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                  razorpay_order_id: razorpayResponse.razorpay_order_id,
                  razorpay_signature: razorpayResponse.razorpay_signature,
                  // Registration data
                  company: formData.company,
                  email: formData.contactEmail,
                  phone: formData.contactPhone,
                  domain: finalDomain(),
                  user_name: formData.user_name,
                  user_email: formData.user_email,
                  password: formData.password,
                  message: formData.registrationMessage || 'Registration from landing page',
                  // Payment metadata for storage
                  amount: totalPaise,
                  currency: 'INR',
                  plan: initialPlan,
                  user_count: userCount,
                }),
              }
            );

            const result = await verifyRes.json();

            if (verifyRes.ok && result.success) {
              setSuccessData({
                company: formData.company,
                domain: finalDomain(),
                adminEmail: formData.user_email,
                paymentId: razorpayResponse.razorpay_payment_id,
              });
              setShowSuccess(true);
            } else {
              let msg = result.message || 'Registration failed after payment. Please contact support.';
              if (result.data) {
                const fieldErrors = Object.values(result.data).flat();
                if (fieldErrors.length > 0) msg = fieldErrors.join('\n');
              }
              alert(msg);
            }
          } catch (err) {
            console.error('Verify error:', err);
            alert(
              'Payment was successful but registration failed. ' +
              'Please contact support with your Payment ID: ' +
              razorpayResponse.razorpay_payment_id
            );
          } finally {
            setIsSubmitting(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        alert('Payment failed: ' + (response.error?.description || 'Unknown error'));
        setIsSubmitting(false);
      });
      rzp.open();

    } catch (err) {
      console.error('Payment error:', err);
      alert('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // ── Success screen ──────────────────────────────────────────────────────────
  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg">
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Payment Successful!</h3>
            <p className="mb-6 text-sm text-gray-600">Your account has been created. You can now log in.</p>

            <div className="p-4 mb-6 space-y-1 text-xs text-left text-gray-600 rounded-md bg-gray-50">
              <p className="mb-2 text-sm font-medium text-gray-900">Login Details</p>
              <p><strong>Company:</strong> {successData.company}</p>
              <p><strong>Login URL:</strong> https://{successData.domain}.apexmed.fun/login</p>
              <p><strong>Email:</strong> {successData.adminEmail}</p>
              <p><strong>Sub-domain:</strong> {successData.domain}</p>
              <p><strong>Payment ID:</strong> {successData.paymentId}</p>
            </div>

            <div className="space-y-3">
              <a
                href={`https://${successData.domain}.apexmed.fun/login`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full px-4 py-3 font-medium text-center text-white transition-colors bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Open Admin Panel
              </a>
              <button
                onClick={handleClose}
                className="w-full px-4 py-3 text-gray-700 transition-colors bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stepTitles = ['Company Details', 'Admin Account', 'Review & Pay'];

  // Subdomain status indicator rendered inline under the domain field
  const SubdomainStatusBadge = () => {
    const effective = finalDomain();
    if (!effective || effective.length < 2) return null;

    if (subdomainStatus === SUBDOMAIN_STATUS.CHECKING) {
      return (
        <p className="flex items-center gap-1 mt-1 text-xs text-gray-500">
          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Checking availability…
        </p>
      );
    }
    if (subdomainStatus === SUBDOMAIN_STATUS.AVAILABLE) {
      return (
        <p className="flex items-center gap-1 mt-1 text-xs text-green-600">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
          </svg>
          <strong>{effective}.apexmed.fun</strong>&nbsp;is available
        </p>
      );
    }
    if (subdomainStatus === SUBDOMAIN_STATUS.TAKEN) {
      return (
        <p className="flex items-center gap-1 mt-1 text-xs text-red-600">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
          <strong>{effective}.apexmed.fun</strong>&nbsp;is already taken
        </p>
      );
    }
    if (subdomainStatus === SUBDOMAIN_STATUS.INVALID) {
      return (
        <p className="flex items-center gap-1 mt-1 text-xs text-red-600">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
          Use only letters, numbers, and hyphens (min 2 chars, no leading/trailing hyphens)
        </p>
      );
    }
    return <p className="mt-1 text-xs text-gray-500">Leave blank to auto-generate from your company name</p>;
  };

  const subdomainBlocked = subdomainStatus === SUBDOMAIN_STATUS.TAKEN || subdomainStatus === SUBDOMAIN_STATUS.INVALID;
  const subdomainChecking = subdomainStatus === SUBDOMAIN_STATUS.CHECKING;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold">
            {initialInterest === 'Demo' ? 'Request a Demo' : stepTitles[currentStep - 1]}
          </h2>
          <button onClick={handleClose} className="text-2xl leading-none text-gray-400 hover:text-gray-600">×</button>
        </div>

        {/* Step indicator */}
        {initialInterest === 'Subscription' && (
          <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors
                  ${currentStep > s ? 'bg-blue-600 text-white' : currentStep === s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {currentStep > s
                    ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                    : s}
                </div>
                {s < 3 && (
                  <div className={`flex-1 h-0.5 rounded transition-colors ${currentStep > s ? 'bg-blue-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* ── Demo form ── */}
          {initialInterest === 'Demo' && (
            <>
              <div>
                <label htmlFor="name" className="block mb-1 text-sm font-medium text-gray-700">Full Name</label>
                <input id="name" value={formData.name} onChange={handleChange} required
                  className="w-full p-3 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your full name" />
              </div>
              <div>
                <label htmlFor="email" className="block mb-1 text-sm font-medium text-gray-700">Email Address</label>
                <input id="email" type="email" value={formData.email} onChange={handleChange} required
                  className="w-full p-3 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your email" />
              </div>
              <div>
                <label htmlFor="company_name" className="block mb-1 text-sm font-medium text-gray-700">Company Name</label>
                <input id="company_name" value={formData.company_name} onChange={handleChange} required
                  className="w-full p-3 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your company name" />
              </div>
              <div>
                <label htmlFor="phone" className="block mb-1 text-sm font-medium text-gray-700">Phone Number</label>
                <input id="phone" type="tel" value={formData.phone} onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your phone number" />
              </div>
              <div>
                <label htmlFor="message" className="block mb-1 text-sm font-medium text-gray-700">Message (Optional)</label>
                <textarea id="message" value={formData.message} onChange={handleChange} rows="3"
                  className="w-full p-3 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Tell us about your requirements..." />
              </div>
            </>
          )}

          {/* ── Step 1: Company Details ── */}
          {initialInterest === 'Subscription' && currentStep === 1 && (
            <>
              <div>
                <label htmlFor="company" className="block mb-1 text-sm font-medium text-gray-700">Company Name *</label>
                <input id="company" value={formData.company} onChange={handleChange} required
                  className="w-full p-3 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., CDEC Pharma" />
              </div>
              <div>
                <label htmlFor="contactEmail" className="block mb-1 text-sm font-medium text-gray-700">Company Email *</label>
                <input id="contactEmail" type="email" value={formData.contactEmail} onChange={handleChange} required
                  className="w-full p-3 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., admin@cdec.com" />
              </div>
              <div>
                <label htmlFor="contactPhone" className="block mb-1 text-sm font-medium text-gray-700">Phone Number *</label>
                <input id="contactPhone" type="tel" value={formData.contactPhone} onChange={handleChange} required
                  className="w-full p-3 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter phone number" />
              </div>
              <div>
                <label htmlFor="domain" className="block mb-1 text-sm font-medium text-gray-700">Sub-domain (Optional)</label>
                <div className={`flex items-center overflow-hidden border rounded-md focus-within:ring-2 focus-within:ring-blue-500
                  ${subdomainBlocked ? 'border-red-400' : subdomainStatus === SUBDOMAIN_STATUS.AVAILABLE ? 'border-green-400' : 'border-gray-300'}`}>
                  <input
                    id="domain"
                    value={formData.domain}
                    onChange={handleDomainChange}
                    className="flex-1 p-3 outline-none"
                    placeholder={formData.company ? formData.company.toLowerCase().replace(/\s+/g, '').slice(0, 4) : 'cdec'}
                    maxLength={50}
                  />
                  <span className="px-3 py-3 text-sm text-gray-500 border-l border-gray-300 bg-gray-50 whitespace-nowrap">.apexmed.fun</span>
                </div>
                <SubdomainStatusBadge />
              </div>
            </>
          )}

          {/* ── Step 2: Admin Account ── */}
          {initialInterest === 'Subscription' && currentStep === 2 && (
            <>
              <div>
                <label htmlFor="user_name" className="block mb-1 text-sm font-medium text-gray-700">Admin Full Name *</label>
                <input id="user_name" value={formData.user_name} onChange={handleChange} required
                  className="w-full p-3 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Navneet Singh" />
              </div>
              <div>
                <label htmlFor="user_email" className="block mb-1 text-sm font-medium text-gray-700">Admin Login Email *</label>
                <input id="user_email" type="email" value={formData.user_email} onChange={handleChange} required
                  className="w-full p-3 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., developer@cdec.com" />
                <p className="mt-1 text-xs text-gray-500">This email will be used to log in to the admin panel</p>
              </div>
              <div>
                <label htmlFor="password" className="block mb-1 text-sm font-medium text-gray-700">Admin Password *</label>
                <input id="password" type="password" value={formData.password} onChange={handleChange} required
                  className="w-full p-3 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Choose a strong password" />
              </div>
              <div>
                <label htmlFor="registrationMessage" className="block mb-1 text-sm font-medium text-gray-700">Additional Notes (Optional)</label>
                <textarea id="registrationMessage" value={formData.registrationMessage} onChange={handleChange} rows="2"
                  className="w-full p-3 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Any additional information..." />
              </div>
            </>
          )}

          {/* ── Step 3: Review & Pay ── */}
          {initialInterest === 'Subscription' && currentStep === 3 && (
            <>
              {/* User count → amount calculation */}
              {pricePerUser > 0 && (
                <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <label className="block mb-3 text-sm font-medium text-blue-900">
                    Number of Users
                  </label>
                  <div className="flex items-center gap-3">
                    <button type="button"
                      onClick={() => setUserCount(c => Math.max(1, c - 1))}
                      className="flex items-center justify-center text-lg font-bold text-blue-700 transition-colors bg-white border border-blue-300 rounded-full w-9 h-9 hover:bg-blue-100">
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={userCount}
                      onChange={(e) => setUserCount(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-16 p-2 font-semibold text-center border border-blue-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button type="button"
                      onClick={() => setUserCount(c => c + 1)}
                      className="flex items-center justify-center text-lg font-bold text-blue-700 transition-colors bg-white border border-blue-300 rounded-full w-9 h-9 hover:bg-blue-100">
                      +
                    </button>
                    <span className="ml-1 text-sm text-blue-700">
                      × ₹{pricePerUser}/user/month
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-3 mt-3 border-t border-blue-200">
                    <span className="text-sm font-medium text-blue-900">Total (first month)</span>
                    <span className="text-xl font-bold text-blue-900">₹{totalINR.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              )}

              {/* Registration summary */}
              <div className="p-4 space-y-1 text-xs text-gray-600 rounded-lg bg-gray-50">
                <p className="mb-2 text-sm font-medium text-gray-700">Registration Summary</p>
                <p><strong>Plan:</strong> {initialPlan}</p>
                <p><strong>Company:</strong> {formData.company}</p>
                <p><strong>Domain:</strong> {finalDomain()}.apexmed.fun</p>
                <p><strong>Admin:</strong> {formData.user_name} ({formData.user_email})</p>
                <p><strong>Contact:</strong> {formData.contactEmail}</p>
              </div>

              {/* Security badge */}
              <div className="flex items-center gap-2 p-3 text-xs text-gray-500 rounded-md bg-gray-50">
                <svg className="flex-shrink-0 w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Secured by Razorpay · Supports UPI, cards, net banking &amp; wallets
              </div>
            </>
          )}

          {/* ── Actions ── */}
          <div className="flex gap-3 pt-2">
            {initialInterest === 'Subscription' && currentStep > 1 && (
              <button type="button" onClick={() => setCurrentStep(currentStep - 1)}
                className="flex-1 py-3 text-gray-700 transition-colors border border-gray-300 rounded-md hover:bg-gray-50">
                ← Back
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting || (currentStep === 1 && (subdomainBlocked || subdomainChecking))}
              title={
                currentStep === 1 && subdomainBlocked
                  ? 'Choose a different sub-domain before continuing'
                  : currentStep === 1 && subdomainChecking
                  ? 'Checking subdomain availability…'
                  : undefined
              }
              className="flex-1 py-3 font-medium text-white transition-colors bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {isSubmitting
                ? (currentStep === 3 ? 'Opening Payment...' : 'Please wait...')
                : currentStep === 1 && subdomainChecking
                ? 'Checking…'
                : initialInterest === 'Demo'
                  ? 'Request Demo'
                  : currentStep === 3
                    ? `Pay ₹${totalINR.toLocaleString('en-IN')} & Register`
                    : 'Next →'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default LeadForm;
